import { useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'expo-router';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import type { WorkoutRecord } from '~/powersync/generated/schema';
import { WorkoutCompletionStatus, WorkoutUtils } from '@fytness/shared';
import { WorkoutProgressView } from '~/workout/components/workout-progress-view/WorkoutProgressView';
import { WorkoutSummary } from '~/workout/components/workout-summary/WorkoutSummary';

export interface WorkoutProgressProps {
  workoutNiceId: string;
}

export const WorkoutProgress = ({ workoutNiceId }: WorkoutProgressProps) => {
  const { t } = useTranslation('workout');

  // Load existing workout data using clean local-api
  const { data: existingWorkout, isLoading: loadingWorkout } =
    useLivePowerSyncQuery<WorkoutRecord>(
      db.Workout.queries.findByNiceId(workoutNiceId),
      [workoutNiceId],
      { single: true },
    );

  const completionStatus = existingWorkout
    ? WorkoutUtils.getCompletionStatus(existingWorkout)
    : null;

  // Reaching /progress with a PLANNED workout is normally a stale-cache transient of the start
  // flow: the started clone inherits the template's niceId, and the shared watch-query cache
  // briefly serves the just-superseded (soft-deleted) planned template before the watch
  // re-emits the clone. Defer the builder redirect a settle-tick — if the query resolves to a
  // started/finished workout we render that (→ the summary) instead of bouncing to the builder.
  const [redirectPlanned, setRedirectPlanned] = useState(false);
  useEffect(() => {
    if (completionStatus !== WorkoutCompletionStatus.PLANNED) {
      setRedirectPlanned(false);
      return;
    }
    const timer = setTimeout(() => setRedirectPlanned(true), 750);
    return () => clearTimeout(timer);
  }, [completionStatus, existingWorkout?.id]);

  // Check workout completion status
  if (existingWorkout) {
    if (completionStatus === WorkoutCompletionStatus.FINISHED) {
      // Show workout summary for finished workouts
      return (
        <ScrollView className="flex-1 bg-background">
          <View className="p-4">
            <WorkoutSummary.Synced
              workoutId={existingWorkout.id}
              className="mb-4"
            />
          </View>
        </ScrollView>
      );
    }
    if (completionStatus === WorkoutCompletionStatus.PLANNED) {
      // Not yet settled — wait rather than bounce on a possibly-stale cache read.
      if (!redirectPlanned) {
        return (
          <ScrollView className="flex-1 bg-background">
            <View className="p-4">
              <Card>
                <CardContent>
                  <Text className="text-muted-foreground">
                    {t('progress.loading')}
                  </Text>
                </CardContent>
              </Card>
            </View>
          </ScrollView>
        );
      }
      return <Redirect href={`/workouts/${workoutNiceId}`} />;
    }
  }

  if (loadingWorkout) {
    return (
      <ScrollView className="flex-1 bg-background">
        <View className="p-4">
          <Card>
            <CardContent>
              <Text className="text-muted-foreground">
                {t('progress.loading')}
              </Text>
            </CardContent>
          </Card>
        </View>
      </ScrollView>
    );
  }

  if (!existingWorkout) {
    return (
      <ScrollView className="flex-1 bg-background">
        <View className="p-4">
          <Card>
            <CardContent>
              <Text className="text-destructive">
                {t('progress.workoutNotFound')}
              </Text>
            </CardContent>
          </Card>
        </View>
      </ScrollView>
    );
  }

  return <WorkoutProgressView.Synced workout={existingWorkout} />;
};
